<template>
  <div class="section">
    <b-loading isFullPage :active="isLoading" />
    <template>
      <h1 class="title mb-15 is-size-5 has-text-color-7">
        {{
          id
            ? App.ModelsScreen.DOCUMENT_MODEL_EDITION
            : App.ModelsScreen.DOCUMENT_MODEL_CREATION
        }}
      </h1>
      <div class="document-header px-20 py-5">
        <ValidationObserver
          v-slot="{ handleSubmit }"
          tag="div"
          class="flex-1"
          :class="{ 'document-info': isMobile }"
        >
          <div class="user-info">
            <ValidationProvider
              v-slot="{ errors }"
              rules="required|max:70"
              class="is-full"
            >
              <b-field
                :type="
                  errors.length
                    ? 'is-refused'
                    : isContrast
                    ? 'is-white'
                    : 'is-blue-oil'
                "
              >
                <template v-if="!isMobile" #label>
                  <span class="has-text-color-8">{{
                    General.TEXTS.MODEL_NAME
                  }}</span>
                  <span class="optional-label ml-5">{{
                    General.OBLIGATORY
                  }}</span>
                </template>
                <section class="is-flex">
                  <div v-if="!isMobile">
                    <b-icon
                      class="document-icon mr-5 pt-10"
                      :type="isContrast ? 'is-white' : 'is-blue-oil'"
                      icon="file-document"
                      size="mdi-30px"
                    />
                  </div>
                  <b-input
                    v-model="model.name"
                    :placeholder="General.PLACEHOLDER.MODEL_NAME"
                    expanded
                    class="flex-1"
                  />
                </section>
                <template #message>
                  <ErrorField :errors="errors" />
                </template>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="actions">
            <b-button
              class="button-size"
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              outlined
              @click="showModalModels"
            >
              {{ General.CLONE_MODELS }}
            </b-button>
            <b-dropdown
              aria-role="list"
              position="is-bottom-right"
              scrollable
              :maxHeight="200"
              :expanded="isMobile"
            >
              <b-button
                slot="trigger"
                slot-scope="{ active }"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                outlined
                :iconRight="active ? 'chevron-up' : 'chevron-down'"
                :expanded="isMobile"
              >
                {{
                  model.type
                    ? model.type.type
                    : App.ModelsScreen.SELECT_A_DOCUMENT_TYPE
                }}
              </b-button>

              <b-dropdown-item
                v-for="type in fileTypes"
                :key="type.id"
                class="custom-dropdown-color"
                aria-role="listitem"
                @click="onChangeType(type)"
              >
                {{ type.type }}
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              outlined
              @click="showApplicabilityModal"
            >
              {{ General.CONFIG }}
            </b-button>
            <b-button
              class="button-size"
              :type="isContrast ? 'is-white' : 'is-green-lawn'"
              :disabled="isLoading"
              @click="handleSubmit(onSubmitDocument)"
            >
              <span class="has-text-weight-semibold">
                {{ General.SAVE }}
              </span>
            </b-button>
            <b-button
              class="button-size"
              :type="isContrast ? 'is-white' : 'is-orange'"
              outlined
              @click="$router.back()"
            >
              {{ General.CANCEL }}
            </b-button>
          </div>
        </ValidationObserver>
      </div>

      <div>
        <iframe
          v-if="model.gdocs_id"
          :key="model.gdocs_id"
          class="gdocs-container"
          title="Janela de edição de documento oficial GDocs"
          :src="
            `https://docs.google.com/document/d/${model.gdocs_id}/edit?usp=sharing&overridemobile=true&rm=embedded`
          "
        ></iframe>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { App, General } from "@/utils/constants";

import ModalModels from "./ModalModels";
import ModalEditorApplicability from "./ModalEditorApplicability";
import ModalWarning from "@/views/App/ModalWarning.vue";

export default {
  name: "ModelEditor",
  props: {
    id: { type: String, default: "" }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      model: { type: null }
    };
  },

  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
      getFileTypeById: "getFileTypeById",
      getFileTypes: "getFileTypes",
      getGDocModel: "getGDocModel",
      getModel: "getModel",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    fileTypes() {
      return this.getFileTypes.filter(type => type.layout_template);
    }
  },

  async beforeMount() {
    this.fileTypesRequest({});
    if (this.id) {
      this.loadModel();
    }
  },

  destroyed() {
    this.$store.commit("setGDocModel", {});
  },

  methods: {
    ...mapActions({
      createModelRequest: "createModelRequest",
      editModelRequest: "editModelRequest",
      fileTypesRequest: "fileTypesRequest",
      createGDocModelRequest: "createGDocModelRequest",
      getModelbyIdRequest: "getModelbyIdRequest"
    }),

    onChangeType(type) {
      if (this.model.type && this.model.type.id !== type.id) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: this.General.WISH_TO_CONTINUE,
            message: this.App.ModelEditorScreen.MESSAGES.CHANGE_TYPE,
            confirm: () => {
              this.model.type = type;
              this.newModel();
            }
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.model.type = type;
        this.newModel();
      }
    },
    validateFile() {
      const msg = [];

      if (!this.model?.all_parts && !this.model?.parts?.length)
        msg.push(this.App.ModelsScreen.MESSAGES.EMPTY_CONFIG);

      if (!this.model.type || !this.model.type.id)
        msg.push(this.General.MESSAGES.EMPTY_TYPE);

      if (msg.length > 0) {
        this.snackToast.open({
          message: `${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    showModalModels() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalModels,
        props: {
          onSelect: model => this.applyModel({ ...model, id: undefined }),
          showOnlyPrimaryTypes: false
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showApplicabilityModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalEditorApplicability,
        props: {
          modelParts: {
            all_parts: this.model.all_parts,
            parts: this.model.parts
          },
          onSetParts: parts => {
            this.model = { ...this.model, ...parts };
          }
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: false
      });
    },

    async newModel() {
      this.isLoading = true;

      if (this.validateNewModel())
        await this.createGDocModelRequest(this.model.type.id);

      this.isLoading = false;

      this.model.gdocs_id = this.getGDocModel.gdocs_id;
    },
    validateNewModel() {
      const msg = [];

      if (!this.model.type || !this.model.type.id)
        msg.push(this.General.MESSAGES.EMPTY_TYPE);

      if (msg.length > 0) {
        this.snackToast.open({
          message: `${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    applyModel(model) {
      const type = this.getFileTypeById(model.document_type);
      this.model = { ...model, type: type, gdocs_id: model.gdocs_model_id };
    },

    async loadModel() {
      this.isLoading = true;
      const status = await this.getModelbyIdRequest({ modelId: this.id });
      if (!status) {
        this.$router.push("/app/admin/modelos");
      } else {
        this.applyModel(this.getModel);
      }
      this.isLoading = false;
    },

    async onSubmitDocument() {
      if (this.validateFile()) {
        this.isLoading = true;

        if (!this.model.name.length) {
          this.isLoading = false;
          this.snackToast.open({
            message: `${General.PLACEHOLDER.MODEL_NAME}.`,
            type: "is-danger",
            duration: 8000
          });
          return false;
        }

        const newModel = {
          id: this.model.id,
          name: this.model.name,
          documentType: this.model.type.id,
          parts: this.model.parts,
          all_parts: this.model.all_parts,
          gdocsId: this.model.gdocs_id
        };

        const results = this.model.id
          ? await this.editModelRequest(newModel)
          : await this.createModelRequest(newModel);

        this.isLoading = false;

        if (results.kind === "ok") this.$router.push("/app/admin/modelos");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding-top: 20px;
  min-height: 75vh;
}

.document-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-color: $border-color;
  .actions {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
    gap: 10px;
  }

  @media screen and (max-width: $tablet) {
    border: none;
    padding: 0 !important;
    .actions {
      flex-direction: column;
      padding-bottom: 15px;
      border-bottom: 1px solid $border-color;
    }
  }
}

.document-info {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
}

.user-info {
  display: flex;
  flex-direction: row;
  align-items: center;

  @media screen and (max-width: $tablet) {
    margin-top: 15px;
  }
}

.gdocs-container {
  width: 100%;
  height: 700px;
}

.button-size {
  min-width: 130px;
}

.optional-label {
  font-size: 12px;
  font-weight: 300;
  color: $color-13;
}
</style>
